import { MyCartAction_PlaceOrder } from "../../cart/types";
import { AdyenBrowserInfo } from "@swo/shared";
import { fetchApi, fetchApiJson, fetchApiJson2, getEndpointForFetch } from "../../data-layer/fetch-api";
import type {
  CustomerOrderPayAgainDetailsResponse,
  PlaceOrderActionError,
  PlaceOrderActionOutput,
  PlaceOrderEffectBody,
} from "@swo/storefront";
import {
  CustomerOrderPaymentStatusResponse,
  CustomerOrderStatusResponse,
  OrderPayAgainBodyPayload,
} from "@swo/storefront";
import { identity } from "fp-ts/lib/function";
import * as E from "fp-ts/lib/Either";

export class StorefrontCheckoutClient {
  constructor(
    private readonly accessToken: string,
    private readonly localeCode: string,
    private readonly baseUrl?: URL,
  ) {}

  static getPlaceOrderBody(returnUrl: string) {
    function getTealiumId(): string | undefined {
      try {
        if ("Tealium" in window && typeof window.Tealium === "function") {
          const id = window.Tealium().getVisitorId();

          if (typeof id === "string") {
            return id;
          }
        }

        return undefined;
      } catch {
        return undefined;
      }
    }

    return {
      returnUrl: returnUrl,
      origin: window.location.origin,
      browserInfo: StorefrontCheckoutClient.getBrowserInfo(),
      intergrations: {
        tealiumId: getTealiumId(),
      },
    };
  }

  placeOrder = (
    action: MyCartAction_PlaceOrder,
    cartRef: { id: string; version: number },
  ): Promise<PlaceOrderActionOutput> => {
    const body: PlaceOrderEffectBody = {
      ...StorefrontCheckoutClient.getPlaceOrderBody(action.returnUrl),
      payment: action.payment,
      localeCode: this.localeCode,
      cartVersion: cartRef.version,
    };

    return fetchApiJson2<PlaceOrderActionOutput>(
      E.left(identity<PlaceOrderActionError>({ tag: "unknown-error" })),
      getEndpointForFetch(`/storefront/shop/from-cart/${cartRef.id}`, this.baseUrl),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${this.accessToken}`,
        },
        body: JSON.stringify(body),
      },
    );
  };

  payAgain = (
    orderId: string,
    returnUrl: URL,
    data: MyCartAction_PlaceOrder["payment"],
  ): Promise<PlaceOrderActionOutput> => {
    const body: OrderPayAgainBodyPayload = {
      ...StorefrontCheckoutClient.getPlaceOrderBody(returnUrl.toString()),
      payment: data,
      localeCode: this.localeCode,
    };

    return fetchApiJson<PlaceOrderActionOutput>(
      getEndpointForFetch(`/storefront/shop/orders/${orderId}/payments`, this.baseUrl),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${this.accessToken}`,
        },
        body: JSON.stringify(body),
      },
    );
  };

  finalizePaymentWithRedirectResult = (data: { paymentId: string; orderId: string; redirectResult: string }) => {
    return this.finalizeCall(
      { paymentId: data.paymentId },
      {
        redirectResult: data.redirectResult,
        orderId: data.orderId,
        type: "redirect-result",
      },
    );
  };

  finalize3DSecure2Payment = (data: { paymentId: string; orderId: string; threeDSResult: string }) => {
    return this.finalizeCall(
      { paymentId: data.paymentId },
      {
        threeDSResult: data.threeDSResult,
        orderId: data.orderId,
        type: "3D Secure 2",
      },
    );
  };

  finalizePaypal = (obj: {
    paymentId: string;
    orderId: string;
    additionalDetails: { details: unknown; paymentData: string };
  }) => {
    return this.finalizeCall(
      { paymentId: obj.paymentId },
      {
        orderId: obj.orderId,
        details: obj.additionalDetails.details,
        paymentData: obj.additionalDetails.paymentData,
        type: "paypal",
      },
    );
  };

  makeApplePayPayment = (
    context: { paymentId: string; orderId: string },
    applePaymentToken: string,
    data: { linkToPurchaseConfirmation: string },
  ) => {
    return this.finalizeCall(
      { paymentId: context.paymentId },
      {
        orderId: context.orderId,
        token: applePaymentToken,
        type: "Apple Pay",
        ...StorefrontCheckoutClient.getPlaceOrderBody(data.linkToPurchaseConfirmation),
      },
    );
  };

  getOrderPaymentStatus(
    data: { orderId: string; paymentId: string },
    localeCode: string,
    abortSignal?: AbortSignal,
  ): Promise<CustomerOrderPaymentStatusResponse> {
    return fetchApi<CustomerOrderPaymentStatusResponse>(
      getEndpointForFetch(
        `/storefront/shop/orders/${data.orderId}/payments/${data.paymentId}?localeCode=${localeCode}`,
        this.baseUrl,
      ),
      { ...this.authorizedOptionsGetJson, signal: abortSignal },
    );
  }

  getOrderStatus(data: { orderId: string }): Promise<CustomerOrderStatusResponse> {
    return fetchApi<CustomerOrderStatusResponse>(
      getEndpointForFetch(`/storefront/shop/orders/${data.orderId}?localeCode=${this.localeCode}`, this.baseUrl),
      this.authorizedOptionsGetJson,
    );
  }

  getOrderPayAgainDetails(
    data: { orderId: string },
    localeCode: string,
  ): Promise<CustomerOrderPayAgainDetailsResponse> {
    return fetchApi<CustomerOrderPayAgainDetailsResponse>(
      getEndpointForFetch(
        `/storefront/shop/orders/${data.orderId}/pay-again-details?localeCode=${localeCode}`,
        this.baseUrl,
      ),
      this.authorizedOptionsGetJson,
    );
  }

  private optionsGetJson = {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  };

  private authorizedOptionsGetJson = {
    ...this.optionsGetJson,
    headers: { ...this.optionsGetJson.headers, authorization: `Bearer ${this.accessToken}` },
  };

  private finalizeCall(context: { paymentId: string }, bodyObj: Record<string, unknown>) {
    return fetchApiJson<"success" | "error">(
      getEndpointForFetch(`/storefront/shop/payments/${context.paymentId}/finalize`, this.baseUrl),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${this.accessToken}`,
        },
        body: JSON.stringify({ ...bodyObj, localeCode: this.localeCode }),
      },
    );
  }

  private static getBrowserInfo(): Omit<AdyenBrowserInfo, "acceptHeader"> {
    return {
      colorDepth: window.screen.colorDepth,
      javaEnabled: navigator.javaEnabled(),
      language: navigator.language,
      screenHeight: window.innerHeight,
      screenWidth: window.innerWidth,
      timeZoneOffset: new Date().getTimezoneOffset(),
      userAgent: navigator.userAgent,
    };
  }
}
